import React from 'react';
import {Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import ReactSelect, {SingleValue} from 'react-select';
import {useGetGraph} from '../../../../../Domains/Service/Hooks/MonitoringManager';
import {
  DEFAULT_GRAPH_PERIOD_HOURS,
  GRAPH_PERIODS,
  GraphServiceType,
} from '../../../../../Domains/Service/Types/graphPeriods';
import styles from './config-monitoring.module.scss';

interface GraphPopoverContentProps {
  buId: string;
  serviceType: GraphServiceType;
  title: string;
  onClose: () => void;
}

interface PeriodOption {
  value: number;
  label: string;
}

const PERIOD_LABEL_KEY: Record<number, string> = {
  2: 'MONITORING.PERIOD.2H',
  24: 'MONITORING.PERIOD.24H',
  72: 'MONITORING.PERIOD.3D',
  168: 'MONITORING.PERIOD.7D',
  744: 'MONITORING.PERIOD.31D',
};

const GraphPopoverContent = ({
  buId,
  serviceType,
  title,
  onClose,
}: GraphPopoverContentProps): JSX.Element => {
  const intl = useIntl();
  const [periodHours, setPeriodHours] = React.useState<number>(DEFAULT_GRAPH_PERIOD_HOURS);
  const {data, isLoading, isError} = useGetGraph(buId, serviceType, periodHours, true);

  const periodOptions = React.useMemo<PeriodOption[]>(
    () =>
      GRAPH_PERIODS.map((period) => ({
        value: period.hours,
        label: intl.formatMessage({id: PERIOD_LABEL_KEY[period.hours] ?? 'MONITORING.PERIOD.24H'}),
      })),
    [intl]
  );

  return (
    <div className={styles.tooltipContent}>
      <div className={styles.tooltipTitle}>
        <span>{title}</span>
        <button
          type="button"
          className={styles.tooltipCloseButton}
          onClick={onClose}
          aria-label={intl.formatMessage({id: 'MONITORING.GRAPH.CLOSE'})}
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>
      <div style={{padding: '8px 16px 0'}}>
        <ReactSelect<PeriodOption, false>
          isSearchable={false}
          value={periodOptions.find((option) => option.value === periodHours) ?? null}
          onChange={(option: SingleValue<PeriodOption>) => option && setPeriodHours(option.value)}
          options={periodOptions}
          classNamePrefix="graph-period-select"
        />
      </div>
      {isLoading && (
        <div className="text-center p-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {isError && (
        <div className="text-center p-4 text-danger">
          {intl.formatMessage({id: 'LIEN.DETAIL.LATENCE.ERROR'})}
        </div>
      )}
      {!isLoading && !isError && data?.base64 && (
        <img
          src={`data:image/png;base64,${data.base64}`}
          alt="CELESTE Monitoring"
          className={styles.graphImage}
        />
      )}
      {!isLoading && !isError && !data?.base64 && (
        <div className="text-center p-4 text-muted">
          {intl.formatMessage({id: 'MONITORING.GRAPH.UNAVAILABLE'})}
        </div>
      )}
    </div>
  );
};

export default GraphPopoverContent;
